import { HttpInterceptorFn, HttpErrorResponse, HttpContextToken, HttpResponse } from '@angular/common/http';
import { inject, Injector } from '@angular/core';
import { UserStore } from '../stores/user.store';
import { AuthService } from '../services/auth.service';
import { TranslationService } from '../services/translation.service';
import { catchError, tap, throwError } from 'rxjs';

export const SKIP_AUTH = new HttpContextToken<boolean>(() => false);

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const userStore = inject(UserStore);
  const authService = inject(AuthService);
  // Resolved lazily: TranslationService loads its JSON through HttpClient, so injecting it
  // up front would be circular on its own requests.
  const injector = inject(Injector);
  const token = userStore.token();
  const wasAuthenticated = !!token;

  if (req.context.get(SKIP_AUTH)) {
    return next(req);
  }

  // If token exists, update the request and add Authorization header
  if (token) {
    req = req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  // If no token, proceed with original request + handle errors
  return next(req).pipe(
    tap((event) => {
      if (!(event instanceof HttpResponse)) return;
      noteBlockedLogout(event.headers.get('X-Blocked-Logout-At'), authService);
    }),
    catchError((err: HttpErrorResponse) => {
      if (wasAuthenticated && isBlockedAccount(err)) {
        authService.logout();
      }
      return handleErrors(err, authService, injector, wasAuthenticated);
    }),
  );
};

let blockedLogoutTimer: ReturnType<typeof setTimeout> | null = null;
let blockedLogoutAt: string | null = null;

function noteBlockedLogout(header: string | null, authService: AuthService): void {
  if (!header) return;
  if (header === 'none') {
    if (blockedLogoutTimer) clearTimeout(blockedLogoutTimer);
    blockedLogoutTimer = null;
    blockedLogoutAt = null;
    return;
  }
  if (blockedLogoutAt === header) return;
  blockedLogoutAt = header;
  if (blockedLogoutTimer) clearTimeout(blockedLogoutTimer);
  const at = Date.parse(header);
  const wait = Number.isNaN(at) ? 0 : Math.max(0, at - Date.now());
  blockedLogoutTimer = setTimeout(() => authService.logout(), wait);
}

function isBlockedAccount(res: HttpErrorResponse): boolean {
  const message = (res.error as { message?: string } | null)?.message ?? '';
  return res.status === 403 && message.includes('This account has been blocked');
}

function handleErrors(
  res: HttpErrorResponse,
  authService: AuthService,
  injector: Injector,
  wasAuthenticated: boolean,
) {
  const defaultErrorMessage = {
    message: 'Pri obdelavi vaše zahteve je prišlo do težave. Prosimo poskusite znova.',
  };

  // client-side error (Network problems and front-end code errors)
  if (res.error instanceof ErrorEvent) {
    return throwError(() => (res.error.message ? res.error : defaultErrorMessage));
  }

  // 401 unauthorized — only force logout when an authenticated request's token was rejected
  // (a real expired session). Anonymous 401s (e.g. the public landing page hitting a
  // protected list endpoint) must not redirect to login.
  if (res.status === 401 && wasAuthenticated) {
    authService.logout();
  }

  if (res.status === 500) {
    const apiErr = res.error as { name?: string; message?: string } | null;
    if (apiErr?.name === 'CastError' && typeof apiErr.message === 'string') {
      return throwError(() => ({ message: apiErr.message, code: 'CAST_ERROR' }));
    }
    return throwError(() => defaultErrorMessage);
  }

  // The API's bad-word check answers in English; show it in the user's language.
  if (res.error?.error === 'PROFANITY_DETECTED') {
    return throwError(() => ({ ...res.error, message: injector.get(TranslationService).instant('common.profanityDetected') }));
  }

  return throwError(() => (res.error.message ? res.error : defaultErrorMessage));
}
